// Der Spielzustand im Client.
//
// **Ein** mutables Singleton, **ein** Einstiegspunkt fuer abgeleitete Werte,
// **ein** Persistenzpfad (Gates FG-26, FG-28). Alle drei Zahlwoerter sind der
// Vertrag — mit dem Umbau haben sie eine neue Bedeutung bekommen:
//
// * Die Wahrheit liegt jetzt beim Server. Der Client haelt eine Kopie und
//   ersetzt sie, wenn der Server eine neue schickt.
// * `localStorage` ist nur noch **Zwischenspeicher fuer den Solo-Modus** und
//   fuer die Ton-Einstellung. Ein Online-Spielstand wird nie daraus geladen —
//   sonst haette ein Spieler zwei Staende und der aeltere gewaenne manchmal.
//
// `try/catch` gibt es hier und in `net/` — nirgends sonst (Gate FG-13). Ueberall
// sonst sollen Fehler laut sein.

import { clamp } from '../core/math';
import {
  balancing, booster, booster as boosterById, cacheById, fleetOrder, gearById, perk,
  probeLog, resources, sideQuest,
  statCounter, statCounters,
} from '../data/balancing';
import type { DerivedStats, StatCounter, StatMap } from '../data/types';
import { emptyColony, readColony, type ColonyState } from '../sim/colony';
import { derivedStats, xpForLevel } from '../sim/stats';

export const SAVE_KEY_PREFIX = 'far-mining-save-v';
export const MUTE_KEY = 'far-mining-mute';
export const HINT_KEY_PREFIX = 'far-mining-hint-';
export const ZOOM_KEY = 'far-mining-zoom';

/** Grenzen des Spieler-Zoomversatzes — der Zoom selbst bleibt ganzzahlig. */
export const ZOOM_STEP_MIN = -2;
export const ZOOM_STEP_MAX = 2;

export interface ShipState {
  level: number;
  xp: number;
  energy: number;
  /** Huellenpunkte. Bei null wird zur Kapsel geschleppt — Zeit, nie Fortschritt. */
  hp: number;
  /** Offene Punkte aus Stufenaufstiegen — die gruenen Pfeile am Levelbalken. */
  points: number;
}

/**
 * Die Spielerstatistik.
 *
 * Die Zaehler stehen **nicht** hier als Feldliste, sondern als Tabelle in
 * `balancing.statCounters` — sie ist die Python-Seite, und der Client hat davon
 * nur einen Abzug (Gate FG-36). Ein Zaehler mit `per` ist eine Karte, alle
 * anderen sind Zahlen.
 *
 * `shots`, `dealt` und `crits` sind Gleitkommazahlen: solo zaehlt der Client
 * diskrete Schuesse, online rechnet der Server kontinuierlich. Beide treffen
 * sich im Mittel; die Anzeige rundet.
 */
export type StatsState = Record<string, number | Record<string, number>>;

export interface SaveState {
  v: number;
  res: Record<string, number>;
  totalRes: Record<string, number>;
  levels: Record<string, number>;
  built: number;
  ship: ShipState;
  quests: Record<string, boolean>;
  /**
   * **Altfeld**: Restdauer je Booster.
   *
   * Wird nur noch gelesen, wenn `boosterEnds` ganz fehlt — und mitgeschrieben,
   * damit `SAVE_VERSION` nicht steigen muss (ein Bump löscht jeden Spielstand).
   */
  boosters: Record<string, number>;
  /**
   * Wann ein Booster **endet** — Sekunden seit der Epoche.
   *
   * Vorher stand hier eine Restdauer, und niemand zählte sie herunter: der
   * Client zählte nur seine eigene Kopie, während die Serverrechnung den
   * Booster weiter anwandte — und nach dem nächsten Laden war die volle
   * Laufzeit zurück. Ein Kristallpreis kaufte damit einen Dauerzustand statt
   * einer Entscheidung.
   *
   * Als Zeitpunkt braucht der Ablauf **keinen Tick und keine Verbindung**: die
   * Zeit läuft nach der Uhr weiter, auch mit geschlossenem Browser.
   */
  boosterEnds: Record<string, number>;
  perks: Record<string, number>;
  ignoredOres: Record<string, boolean>;
  /**
   * Das **Wunscherz** der Drohnen — leer heißt „keins", der Ausgangszustand.
   *
   * Eine Neigung, keine Zielauswahl (`GAME-001`): der Spieler wählt, *was* seine
   * Drohnen bevorzugt holen, nicht *welchen* Brocken. Ist keins in Reichweite,
   * graben sie weiter wie bisher. Ein additives Feld — kein `SAVE_VERSION`-Bump.
   */
  wishOre: string;
  seen: Record<string, boolean>;
  /** Geborgene Sonden-Logs — die Einmal-Sperre fuer den vollen Fund. */
  probeLogs: Record<string, boolean>;
  /**
   * Geborgene Fundstellen — dieselbe Einmal-Sperre wie bei den Sonden.
   *
   * Sie ist zugleich der **Kettenstand**: der offene Hinweis ist die erste
   * Fundstelle ihrer Ära, die hier nicht steht. Ein zweites Feld dafür wäre eine
   * zweite Wahrheit, die still davon abweichen kann.
   *
   * Ein additives Feld — ihre Abwesenheit heißt „noch nichts geborgen".
   */
  caches: Record<string, boolean>;
  /**
   * Ab wann eine geborgene Fundstelle wieder etwas hergibt — je ID ein
   * **Zeitpunkt**, nie eine Restdauer (`zustand.md`).
   *
   * Ihre Abwesenheit heißt „sofort"; ein additives Feld, kein Formatwechsel.
   */
  cacheReady: Record<string, number>;
  /**
   * Geholte Fundstücke — der Verladearm und was noch dazukommt.
   *
   * Getrennt von `caches`, weil es etwas anderes ist: eine Kiste gibt **Vorrat**
   * und gibt später wieder etwas her, ein Fundstück gibt ein **Können** und
   * genau einmal. In derselben Karte wäre „schon geborgen" zweierlei, und der
   * Kettenstand (`openCache`) zählte den Arm als Kistenfund mit.
   *
   * Ein additives Feld — seine Abwesenheit heißt „noch nichts geholt".
   */
  gear: Record<string, boolean>;
  /**
   * Zünden **stapelt**, statt zu verlängern — der Schalter des Spielers.
   *
   * Im Spielstand und nicht im Browserspeicher, weil er eine **Regel** ändert
   * und nicht eine Ansicht: der Server wendet Booster an, also muss er ihn
   * kennen. Ton- und Zoomeinstellung liegen aus genau dem umgekehrten Grund
   * lokal (`zustand.md`).
   *
   * Additiv — seine Abwesenheit heißt „verlängern", das bisherige Verhalten.
   */
  boosterStacking: boolean;
  /** Wie hoch ein laufender Booster gestapelt ist — 1 ist der Normalfall. */
  boosterStacks: Record<string, number>;
  /**
   * Erledigte Schritte je Nebenauftrag — ein **Zähler**, keine Marke.
   *
   * Er wächst nur. Ein additives Feld: seine Abwesenheit heißt „noch keinen
   * Schritt", und das ist eine vollständig gültige Bedeutung.
   */
  sideSteps: Record<string, number>;
  /** Abgeholte Nebenaufträge — die zweite Frage: „schon belohnt". */
  sideClaimed: Record<string, boolean>;
  stats: StatsState;
  finished: boolean;
  /**
   * Der **Akt**, in dem der Spieler steht (`GAME-001`).
   *
   * Additiv — kein `SAVE_VERSION`-Bump. Seine Abwesenheit hat eine gültige
   * Bedeutung: Akt I, genau dort, wo der Stand war.
   */
  era: number;
  /**
   * Die Kolonie von Akt III — Planeten, Anlagen, Güter (`COLONY-001`).
   *
   * Additiv — kein `SAVE_VERSION`-Bump. Ihre Abwesenheit heißt „noch nicht
   * gelandet". Die Güter stehen **hier** und nie in `res`.
   */
  colony: ColonyState;
}

function emptyRes(): Record<string, number> {
  return Object.fromEntries(resources().map((entry) => [entry.id, 0]));
}

export function emptyStats(): StatsState {
  const out: StatsState = {};
  for (const counter of statCounters()) out[counter.id] = counter.per ? {} : 0;
  return out;
}

export function emptySave(): SaveState {
  return {
    v: balancing().saveVersion,
    res: emptyRes(),
    totalRes: emptyRes(),
    levels: {},
    built: 0,
    ship: { level: 1, xp: 0, energy: balancing().base.energyMax, hp: balancing().base.hullMax, points: 0 },
    quests: {},
    boosters: {},
    boosterEnds: {},
    perks: {},
    ignoredOres: {},
    wishOre: '',
    seen: {},
    probeLogs: {},
    caches: {},
    gear: {},
    boosterStacking: false,
    boosterStacks: {},
    cacheReady: {},
    sideSteps: {},
    sideClaimed: {},
    stats: emptyStats(),
    finished: false,
    era: balancing().eraFirst,
    colony: emptyColony(),
  };
}

/**
 * Die Uhr, gegen die zeitgebundene Wirkungen enden — Sekunden seit der Epoche.
 *
 * Dieselbe Zeitbasis wie auf dem Server (`time.time()`), damit ein Ende, das
 * dort gesetzt wurde, hier dasselbe bedeutet. Bewusst **nicht** eine
 * Bildschirmuhr: die Zeit läuft auch mit geschlossenem Browser weiter.
 */
export const nowSeconds = (): number => Date.now() / 1000;

/**
 * Endzeitpunkte aus Fremdinhalt.
 *
 * Ein unmöglicher Wert gilt als **abgelaufen** und wirft nicht (Gate FG-27):
 * negativ, kaputt, oder weiter in der Zukunft, als ein gestapelter Booster
 * plausibel reichen kann (`boosterMaxAhead`).
 */
function boosterEnds(raw: Record<string, unknown>, now = nowSeconds()): Record<string, number> {
  const out: Record<string, number> = {};
  const limit = now + balancing().boosterMaxAhead;
  for (const [id, value] of Object.entries(raw)) {
    if (!booster(id)) continue;
    const end = num(value);
    if (end > now && end <= limit) out[id] = end;
  }
  return out;
}

/**
 * Altformat kulant umrechnen: die gespeicherte Restdauer gilt **ab jetzt**.
 *
 * Ohne das verlöre jeder bestehende Spielstand seine laufenden Booster — für
 * einen Kristallpreis, den der Spieler schon bezahlt hat.
 */
function boosterEndsFromLegacy(left: Record<string, number>, now = nowSeconds()): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, rest] of Object.entries(left)) {
    const entry = booster(id);
    if (entry && rest > 0) out[id] = now + Math.min(rest, entry.dur);
  }
  return out;
}

/**
 * Booster aus Fremdinhalt: das Altfeld und die Endzeitpunkte.
 *
 * Die **Anwesenheit** des Endefeldes entscheidet, nicht sein Inhalt: ein neuer
 * Stand mit lauter abgelaufenen Boostern darf nicht auf das Altfeld
 * zurückfallen — das erweckte sie wieder zum Leben.
 */
function readBoosters(data: Record<string, unknown>, out: SaveState): void {
  if (isRecord(data.boosters)) {
    for (const [id, value] of Object.entries(data.boosters)) out.boosters[id] = Math.max(0, num(value));
  }
  out.boosterEnds = isRecord(data.boosterEnds)
    ? boosterEnds(data.boosterEnds)
    : boosterEndsFromLegacy(out.boosters);
}

/**
 * Erzfilter und Wunscherz aus Fremdinhalt.
 *
 * Zwei Regeln, die zusammengehören: der Filter darf **nie alle** ausschließen —
 * ein Schiff, das nichts mehr abbaut, sieht nach einem Fehler aus, nicht nach
 * einer Einstellung —, und ein ausgefiltertes Wunscherz wäre ein Widerspruch:
 * bevorzugt holen und zugleich nicht anfassen.
 */
/**
 * Die Ära aus Fremdinhalt — fehlt sie, ist es Akt I.
 *
 * Geklemmt auf die bekannten Ären: ein Stand mit `era: 99` wäre ein Spieler in
 * einer Welt, die es nicht gibt. Still zurückfallen, nicht werfen (Gate FG-27).
 */
function readEra(raw: unknown): number {
  const data = balancing();
  return Math.max(data.eraFirst, Math.min(data.eraHome, Math.round(num(raw, data.eraFirst))));
}

function readOres(data: Record<string, unknown>, out: SaveState): void {
  if (isRecord(data.ignoredOres)) {
    for (const [id, value] of Object.entries(data.ignoredOres)) {
      if (resources().some((entry) => entry.id === id) && value) out.ignoredOres[id] = true;
    }
    if (Object.keys(out.ignoredOres).length >= resources().length) out.ignoredOres = {};
  }
  if (typeof data.wishOre === 'string'
    && resources().some((entry) => entry.id === data.wishOre)
    && !out.ignoredOres[data.wishOre]) {
    out.wishOre = data.wishOre;
  }
}

/** Die erlaubten Schluessel einer aufgeschluesselten Zaehlerkarte. */
function counterDomain(per: StatCounter['per']): string[] {
  if (per === 'res') return resources().map((entry) => entry.id);
  if (per === 'rockKind') return [...balancing().rockKinds.map((k) => k.id), balancing().richKind.id];
  if (per === 'channel') return balancing().weaponChannels.map((entry) => entry.id);
  if (per === 'enemy') return balancing().enemies.map((entry) => entry.id);
  if (per === 'good') return balancing().goods.map((entry) => entry.id);
  return [];
}

/** Der Flottenauftrag des Feldes, wie der Server ihn zuletzt gemeldet hat. */
export interface FleetState {
  order: string;
  progress: number;
  /** Restsekunden des Feld-Buffs — 0, solange keiner laeuft. */
  buffLeft: number;
}

class Store {
  save: SaveState = emptySave();
  stats: DerivedStats = derivedStats(emptySave());
  revision = 0;
  /** Solo: der Client rechnet selbst. Online: der Server schickt Wahrheit. */
  authoritative = true;
  /**
   * Die Saat des Feldes, in dem gerade geflogen wird.
   *
   * **Fluechtig, nie Teil des Spielstands** (Gate FG-28) — sie gehoert zum Feld,
   * nicht zum Spieler, und der Server nennt sie beim Verbinden. Solo ist es die
   * Saat aus der Tabelle.
   *
   * Sie steht hier, weil die Oberflaeche sie braucht: das Fundbuch rechnet den
   * offenen Hinweis aus dem Ort der naechsten Kiste, und `ui/` darf `scenes/`
   * nicht importieren (Gate FG-04). Ueber den Zustand ist der Kanal ohnehin da.
   */
  fieldSeed: number = balancing().world.defaultSeed;
  /** Zuletzt gesehener Satz aktiver Booster — der Änderungsschlüssel des Ablaufs. */
  private lastActiveBoosters = '';
  /**
   * Flottenauftrag und Feld-Buff — **fluechtig**, nie Teil des Spielstands
   * (Gates FG-27, FG-28): beides gehoert zum Feld, nicht zum Spieler, und der
   * Server meldet es beim Verbinden neu.
   */
  fleet: FleetState | null = null;
  private fleetPerk: StatMap | null = null;
  /**
   * Der **lokale** Zeitpunkt, zu dem der Lagerstand der Kolonie galt — online.
   *
   * Flüchtig, nie Teil des Spielstands: er ist eine Eigenschaft dieser Anzeige.
   * Der Server meldet das **Alter** seines Standes (`goodsAge`), weil der Client
   * keine Serveruhr hat; von hier aus schreibt die Anzeige mit den Raten fort.
   * Solo gilt `colony.t` selbst — das ist dort die eigene Uhr.
   */
  colonyStamp = 0;

  /** Der **einzige** Einstiegspunkt fuer die abgeleiteten Werte. */
  refresh(): void {
    this.stats = derivedStats(this.save, this.fleetPerk);
  }

  /** Die Flottenmeldung des Servers uebernehmen (`fleet`-Nachricht). */
  setFleet(order: string, progress: number, buffEndsIn: number | null): void {
    this.fleet = { order, progress, buffLeft: Math.max(0, buffEndsIn ?? 0) };
    const table = fleetOrder(order);
    this.setFleetPerk(table && this.fleet.buffLeft > 0 ? table.perk : null);
  }

  /** Beim Trennen: das Feld ist weg, also auch Ziel und Buff. */
  clearFleet(): void {
    this.fleet = null;
    this.setFleetPerk(null);
  }

  /** Die Buff-Restzeit weiterzaehlen — die Wahrheit haelt der Server. */
  tickFleet(dt: number): void {
    if (!this.fleet || this.fleet.buffLeft <= 0) return;
    this.fleet.buffLeft = Math.max(0, this.fleet.buffLeft - dt);
    if (this.fleet.buffLeft <= 0) this.setFleetPerk(null);
  }

  private setFleetPerk(perkMap: StatMap | null): void {
    if (perkMap === this.fleetPerk) return;
    this.fleetPerk = perkMap;
    this.refresh();
  }

  /** Einen Spielstand vom Server uebernehmen. Ersetzt, addiert nicht. */
  adopt(save: SaveState, revision: number): void {
    this.save = save;
    this.revision = revision;
    this.colonyStamp = nowSeconds();
    this.refresh();
  }

  /** Den Lagerstand der Kolonie vom Server übernehmen — mit seinem Alter. */
  setGoods(goods: Record<string, number>, age: number): void {
    for (const [gut, menge] of Object.entries(goods)) {
      if (gut in this.save.colony.goods) this.save.colony.goods[gut] = menge;
    }
    this.colonyStamp = nowSeconds() - Math.max(0, age);
  }

  /** Wie lange der Lagerstand schon gilt — die Spanne, um die die Anzeige fortschreibt. */
  colonyElapsed(now = nowSeconds()): number {
    const seit = this.authoritative ? this.save.colony.t : this.colonyStamp;
    return seit > 0 ? Math.max(0, now - seit) : 0;
  }

  reset(): void {
    this.save = emptySave();
    this.revision = 0;
    this.refresh();
  }

  // ── Statistik ──────────────────────────────────────────────────────────────
  //
  // Der Spiegel der Python-Seite. Zaehler wachsen oder stehen — sie fallen nie.

  /** Einen skalaren Zaehler erhoehen (bzw. sein Maximum anheben). */
  count(counterId: string, amount = 1): void {
    if (amount <= 0) return;
    const counter = statCounter(counterId);
    if (!counter) return;
    const current = (this.save.stats[counterId] as number) ?? 0;
    this.save.stats[counterId] = counter.merge === 'max' ? Math.max(current, amount) : current + amount;
  }

  /** Einen aufgeschluesselten Zaehler erhoehen (`rocksByOre`, `shots`, …). */
  countBy(counterId: string, key: string, amount = 1): void {
    if (amount <= 0) return;
    const card = this.save.stats[counterId] as Record<string, number> | undefined;
    if (!card) return;
    card[key] = (card[key] ?? 0) + amount;
  }

  /** Eine Zahl aus der Statistik lesen — mit Rueckfallwert. */
  stat(counterId: string): number {
    return (this.save.stats[counterId] as number) ?? 0;
  }

  /** Eine Zaehlerkarte lesen — mit Rueckfallwert. */
  statCard(counterId: string): Record<string, number> {
    return (this.save.stats[counterId] as Record<string, number>) ?? {};
  }

  // ── Ressourcen ─────────────────────────────────────────────────────────────
  add(resId: string, amount: number): void {
    if (amount <= 0) return;                 // Ressourcen sinken nur durch Kauf
    this.save.res[resId] = (this.save.res[resId] ?? 0) + amount;
    this.save.totalRes[resId] = (this.save.totalRes[resId] ?? 0) + amount;
  }

  canAfford(cost: Record<string, number>): boolean {
    return Object.entries(cost).every(([res, amount]) => (this.save.res[res] ?? 0) >= amount);
  }

  /** Der **einzige** abziehende Pfad — und damit die Stelle fuer `spentRes`. */
  pay(cost: Record<string, number>): boolean {
    if (!this.canAfford(cost)) return false;
    for (const [res, amount] of Object.entries(cost)) {
      this.save.res[res] -= amount;
      this.countBy('spentRes', res, amount);
    }
    return true;
  }

  // ── Erfahrung ──────────────────────────────────────────────────────────────
  addXp(amount: number): number {
    if (amount <= 0) return 0;
    this.save.ship.xp += amount;
    let gained = 0;
    let need = xpForLevel(this.save.ship.level);
    while (this.save.ship.xp >= need) {
      this.save.ship.xp -= need;
      this.save.ship.level++;
      gained++;
      need = xpForLevel(this.save.ship.level);
    }
    if (gained) {
      this.count('levelUps', gained);
      this.refresh();
    }
    return gained;
  }

  get xpNeeded(): number {
    return xpForLevel(this.save.ship.level);
  }

  upgradeLevel(id: string): number {
    return this.save.levels[id] ?? 0;
  }

  get upgradeTotal(): number {
    return Object.values(this.save.levels).reduce((a, b) => a + b, 0);
  }

  /** Welche Booster gerade wirken — eine Frage an die Uhr, kein Zähler. */
  activeBoosters(now = nowSeconds()): string[] {
    const out: string[] = [];
    for (const [id, end] of Object.entries(this.save.boosterEnds)) {
      if (end > now) out.push(id);
    }
    return out;
  }

  /** Restzeit eines Boosters in Sekunden — 0, wenn er nicht (mehr) läuft. */
  boosterLeft(id: string, now = nowSeconds()): number {
    return Math.max(0, (this.save.boosterEnds[id] ?? 0) - now);
  }

  /**
   * Nachsehen, ob ein Booster gerade ausgelaufen ist.
   *
   * **Kein Herunterzählen**: der Ablauf passiert von selbst, hier wird er nur
   * bemerkt, damit die abgeleiteten Werte einmal neu gerechnet werden. Wer die
   * Seite währenddessen neu lädt, verliert dadurch nichts.
   */
  refreshBoosters(): boolean {
    const aktiv = this.activeBoosters().sort().join('|');
    if (aktiv === this.lastActiveBoosters) return false;
    this.lastActiveBoosters = aktiv;
    this.refresh();
    return true;
  }
}

export const store = new Store();

// ── Persistenz (der einzige Pfad, Gate FG-28) ────────────────────────────────
const saveKey = (): string => SAVE_KEY_PREFIX + balancing().saveVersion;

/**
 * Version 4 — die eine Vorversion, die kulant gelesen wird (STATE-001, FG-27).
 *
 * Eingefrorene Tatsachen ueber das **alte** Format, nirgends abgeleitet: dort
 * hiess `built >= 8` „der Teilchenbeschleuniger steht" und `built >= 13` „das
 * Heimatfeuer brennt", und die Navigationskette hat **je drei** Stufen
 * davorgeschoben.
 */
const LEGACY_SAVE_VERSION = 4;
const V4_STARGATE_BUILT = 8;
const V4_HOMEFIRE_BUILT = 13;
const V4_CHAIN_SHIFT = 3;

const legacySaveKey = (): string => SAVE_KEY_PREFIX + LEGACY_SAVE_VERSION;

/**
 * Einen Solo-Stand der Version 4 umrechnen — sonst nichts.
 *
 * Zwei Stufen, weil zwei Ketten gewachsen sind: wer das Heimatfeuer hatte,
 * liegt hinter **beiden** Einschüben. Ohne den Versatz hieße ein alter Stand
 * mit gebautem Beschleuniger plötzlich „Sprungspule", und der Heimflug wäre
 * still wieder verriegelt.
 */
export function migrateV4(save: SaveState): SaveState {
  const letzte = balancing().builds.length;
  if (save.built >= V4_HOMEFIRE_BUILT) {
    save.built = Math.min(letzte, save.built + 2 * V4_CHAIN_SHIFT);
  } else if (save.built >= V4_STARGATE_BUILT) {
    save.built = Math.min(letzte, save.built + V4_CHAIN_SHIFT);
  }
  return save;
}

/**
 * Solo-Spielstand sichern.
 *
 * `localStorage` kann werfen: privater Modus, voller Speicher, verweigerte
 * Berechtigung. Deshalb ist **jeder** Zugriff abgesichert und liefert einen
 * Fehlschlag zurueck, statt das Spiel zu beenden.
 */
export function saveLocal(): boolean {
  try {
    localStorage.setItem(saveKey(), JSON.stringify(store.save));
    return true;
  } catch {
    return false;
  }
}

/** Solo-Spielstand laden. Ein kaputter Stand bringt das Spiel nie zum Absturz. */
export function loadLocal(): boolean {
  let raw: string | null = null;
  let legacy = false;
  try {
    raw = localStorage.getItem(saveKey());
    // Die Version haengt am Schluessel: genau **eine** Vorversion wird kulant
    // gelesen, jede andere bleibt liegen und ergibt einen leeren Stand.
    if (!raw) {
      raw = localStorage.getItem(legacySaveKey());
      legacy = raw !== null;
    }
  } catch {
    return false;
  }
  if (!raw) return false;

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  const clean = sanitize(parsed);
  store.adopt(legacy ? migrateV4(clean) : clean, 0);
  return true;
}

export function wipeLocal(): void {
  try {
    localStorage.removeItem(saveKey());
    // Auch den Altschluessel: sonst erweckte „Neu anfangen" beim naechsten
    // Laden den Stand der Vorversion wieder zum Leben.
    localStorage.removeItem(legacySaveKey());
  } catch {
    // Ein fehlgeschlagenes Loeschen darf den Neustart nicht verhindern.
  }
  store.reset();
}

export function loadMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveMuted(muted: boolean): boolean {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    return true;
  } catch {
    return false;
  }
}

/**
 * Der Zoomversatz des Spielers — wie viele **ganze** Stufen von der aus
 * Fensterhoehe und Sensorik berechneten Stufe abgewichen wird.
 *
 * Eine Eigenschaft dieses Browsers, kein Fortschritt: er gehoert deshalb
 * hierher und nicht in den Spielstand (Gate FG-28), genau wie die
 * Ton-Einstellung. Ein kaputter Wert ergibt 0, nicht einen Absturz.
 */
export function loadZoomStep(): number {
  try {
    return clampZoomStep(Number(localStorage.getItem(ZOOM_KEY)));
  } catch {
    return 0;
  }
}

export function saveZoomStep(step: number): boolean {
  try {
    localStorage.setItem(ZOOM_KEY, String(clampZoomStep(step)));
    return true;
  } catch {
    return false;
  }
}

export function clampZoomStep(step: number): number {
  if (!Number.isFinite(step)) return 0;
  return Math.round(clamp(step, ZOOM_STEP_MIN, ZOOM_STEP_MAX));
}

/**
 * Gesehene Oberflaechen-Hinweise.
 *
 * Bewusst **nicht** in `save.seen`: das ist der Spielstand, und ihn zu
 * schreiben ginge nur ueber einen zweiten Schreibpfad (Gate FG-28). Ein
 * einmal weggeklickter Hinweis ist auch kein Fortschritt, sondern eine
 * Eigenschaft dieses Browsers — genau wie die Ton-Einstellung, und deshalb
 * genau wie sie **hier** und nirgends sonst.
 */
export function hintSeen(id: string): boolean {
  try {
    return localStorage.getItem(HINT_KEY_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

export function markHintSeen(id: string): boolean {
  try {
    localStorage.setItem(HINT_KEY_PREFIX + id, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Fremdinhalt in einen gueltigen Spielstand ueberfuehren.
 *
 * Jedes Feld hat einen Rueckfallwert. Die Zusage lautet nicht „dein Spielstand
 * ueberlebt jede Version", sondern „ein kaputter Spielstand bringt das Spiel
 * nie zum Absturz".
 */
export function sanitize(raw: unknown): SaveState {
  const out = emptySave();
  if (typeof raw !== 'object' || raw === null) return out;
  const data = raw as Record<string, unknown>;

  copyNumbers(data.res, out.res);
  copyNumbers(data.totalRes, out.totalRes);

  if (isRecord(data.levels)) {
    for (const [id, level] of Object.entries(data.levels)) {
      const known = balancing().nodes.find((entry) => entry.id === id);
      if (!known) continue;
      out.levels[id] = Math.max(0, Math.min(known.max, Math.floor(num(level))));
    }
  }
  out.built = Math.max(0, Math.min(balancing().builds.length, Math.floor(num(data.built))));

  if (isRecord(data.ship)) {
    out.ship.level = Math.max(1, Math.floor(num(data.ship.level, 1)));
    out.ship.xp = Math.max(0, num(data.ship.xp));
    out.ship.energy = Math.max(0, num(data.ship.energy, balancing().base.energyMax));
    out.ship.hp = Math.max(0, num(data.ship.hp, balancing().base.hullMax));
    out.ship.points = Math.max(0, Math.floor(num(data.ship.points)));
  }
  if (isRecord(data.quests)) {
    for (const [id, value] of Object.entries(data.quests)) {
      out.quests[id] = isRecord(value) ? Boolean(value.claimed) : Boolean(value);
    }
  }
  readBoosters(data, out);
  if (isRecord(data.perks)) {
    for (const [id, value] of Object.entries(data.perks)) {
      const known = perk(id);
      if (!known) continue;                    // ausgelaufener Punkt — verwerfen
      out.perks[id] = Math.max(0, Math.min(known.max, Math.floor(num(value))));
    }
  }
  readOres(data, out);
  if (isRecord(data.seen)) {
    for (const [id, value] of Object.entries(data.seen)) out.seen[id] = Boolean(value);
  }
  copyProbeLogs(data.probeLogs, out.probeLogs);
  copyCaches(data.caches, out.caches);
  copyGear(data.gear, out.gear);
  out.boosterStacking = Boolean(data.boosterStacking);
  copyStacks(data.boosterStacks, out.boosterStacks);
  copyCacheReady(data.cacheReady, out.cacheReady);
  copySide(data, out);
  copyStats(data.stats, out.stats);
  out.finished = Boolean(data.finished);
  out.era = readEra(data.era);
  out.colony = readColony(data.colony, nowSeconds());
  return out;
}

/**
 * Die Statistik aus Fremdinhalt uebernehmen.
 *
 * Unbekannte Zaehler und unbekannte Kartenschluessel fallen still weg — genau
 * wie ein ausgelaufener Baumknoten. Ein alter Spielstand ohne die neuen Zaehler
 * beginnt bei null; deshalb ist ein neuer Zaehler **kein** Formatwechsel und
 * `saveVersion` bleibt stehen.
 */
function copyStats(raw: unknown, target: StatsState): void {
  if (!isRecord(raw)) return;
  for (const counter of statCounters()) {
    const value = raw[counter.id];
    if (!counter.per) {
      const zahl = Math.max(0, num(value));
      target[counter.id] = counter.kind === 'count' ? Math.floor(zahl) : zahl;
      continue;
    }
    const card: Record<string, number> = {};
    if (isRecord(value)) {
      for (const key of counterDomain(counter.per)) {
        const menge = Math.max(0, num(value[key]));
        if (menge > 0) card[key] = menge;
      }
    }
    target[counter.id] = card;
  }
}

/**
 * Nebenauftraege aus Fremdinhalt.
 *
 * Ein ausgelaufener Auftrag faellt still weg, wie ein ausgelaufener Knoten;
 * ein Wert jenseits der Kettenlaenge waere „fertiger als fertig" und wird
 * heruntergezogen statt zu werfen (Gate FG-27).
 */
function copySide(data: Record<string, unknown>, out: SaveState): void {
  if (isRecord(data.sideSteps)) {
    for (const [id, value] of Object.entries(data.sideSteps)) {
      const known = sideQuest(id);
      if (!known) continue;
      const done = Math.max(0, Math.min(known.steps.length, Math.floor(num(value))));
      if (done > 0) out.sideSteps[id] = done;
    }
  }
  if (isRecord(data.sideClaimed)) {
    for (const [id, value] of Object.entries(data.sideClaimed)) {
      if (sideQuest(id) && value) out.sideClaimed[id] = true;
    }
  }
}

/** Geborgene Fundstellen uebernehmen. Eine ausgelaufene faellt still weg. */
/**
 * Booster-Stapel uebernehmen.
 *
 * Ein unbekannter Booster und eine unmoegliche Hoehe fallen still weg (Gate
 * FG-27) — geklemmt auf 1 bis `boosterMaxStack`, damit ein manipulierter Stand
 * keinen Dauerzustand kauft.
 */
function copyStacks(raw: unknown, target: Record<string, number>): void {
  if (!isRecord(raw)) return;
  for (const [id, value] of Object.entries(raw)) {
    if (!boosterById(id) || typeof value !== 'number' || !isFinite(value)) continue;
    target[id] = Math.max(1, Math.min(balancing().boosterMaxStack, Math.floor(value)));
  }
}

function copyGear(raw: unknown, target: Record<string, boolean>): void {
  if (!isRecord(raw)) return;
  for (const [id, value] of Object.entries(raw)) {
    if (value && gearById(id)) target[id] = true;
  }
}

function copyCaches(raw: unknown, target: Record<string, boolean>): void {
  if (!isRecord(raw)) return;
  for (const [id, value] of Object.entries(raw)) {
    if (value && cacheById(id)) target[id] = true;
  }
}

/**
 * Die Fristen der Fundstellen uebernehmen.
 *
 * Ein unmoeglicher Wert gilt als **abgelaufen** und wirft nicht (Gate FG-27):
 * negativ, kaputt oder weiter voraus als plausibel heisst „sofort wieder da" —
 * dieselbe Kulanz wie beim Booster-Ende.
 */
function copyCacheReady(raw: unknown, target: Record<string, number>): void {
  if (!isRecord(raw)) return;
  const grenze = Date.now() / 1000 + balancing().cache.cooldown;
  for (const [id, value] of Object.entries(raw)) {
    const ende = num(value);
    if (ende > 0 && ende <= grenze && cacheById(id)) {
      target[id] = ende;
    }
  }
}

/** Geborgene Sonden-Logs uebernehmen. Ein ausgelaufenes Log faellt still weg. */
function copyProbeLogs(raw: unknown, target: Record<string, boolean>): void {
  if (!isRecord(raw)) return;
  for (const [id, value] of Object.entries(raw)) {
    if (probeLog(id) && value) target[id] = true;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && isFinite(value) ? value : fallback;
}

function copyNumbers(raw: unknown, target: Record<string, number>): void {
  if (!isRecord(raw)) return;
  for (const [key, value] of Object.entries(raw)) {
    if (key in target) target[key] = Math.max(0, num(value));
  }
}
